import React from 'react';

/**
 * Hero rotation thumbnails — one cube rendered at each of the six
 * rotation values offered by the `heroRotation` seed input (15°, 20°,
 * 25°, 30°, 40°, 45°). The cube is drawn in TRUE 3D with a
 * perspective projection so the front face foreshortens (its far
 * edge compresses, its near edge stays tall) as the rotation grows.
 * That foreshortening is what makes the cube read as actually
 * turning rather than just "widening" — with a purely orthographic /
 * cavalier projection the front face stays a perfect rectangle and
 * no rotation is legible.
 *
 * Design rules:
 *   - 120×90 viewBox, 4:3 aspect.
 *   - Each card: background + floor + ground shadow + 3D cube +
 *     small degree badge pinned top-right.
 *   - Camera: fixed slight tilt down (18°), perspective distance =
 *     5 cube-widths, cube side length = 30 screen units.
 *   - Cube is auto-centered horizontally on CUBE_CX and grounded
 *     so its lowest projected vertex sits at HORIZON.
 */

const baseStyle: React.CSSProperties = {
    width: '100%',
    aspectRatio: '4/3',
    display: 'block',
};

const HORIZON = 62;
const CUBE_CX = 60;
const SCALE = 30;          // screen units per cube-unit
const TILT_X_DEG = 18;     // fixed camera tilt (looking down)
const CAM_DISTANCE = 5;    // in cube-widths
const FOCAL = 5;

type Vec3 = [number, number, number];

const Floor: React.FC<{ fill: string }> = ({ fill }) => (
    <rect x="0" y={HORIZON} width="120" height={90 - HORIZON} fill={fill} />
);

/**
 * Rotate a point around the Y axis, then tilt around the X axis
 * (camera look-down), then perspective-project into 2D screen space
 * relative to the camera. Returns normalized projection coords —
 * caller scales and offsets to place them on the thumbnail.
 */
function project3D(v: Vec3, rotY: number, tiltX: number): [number, number] {
    const [x0, y0, z0] = v;
    const cY = Math.cos(rotY);
    const sY = Math.sin(rotY);
    const x1 = x0 * cY + z0 * sY;
    const z1 = -x0 * sY + z0 * cY;
    const cX = Math.cos(tiltX);
    const sX = Math.sin(tiltX);
    const y2 = y0 * cX + z1 * sX;
    const z2 = -y0 * sX + z1 * cX;
    const zCam = CAM_DISTANCE + z2;
    return [x1 * FOCAL / zCam, y2 * FOCAL / zCam];
}

/** Small dark pill with the degree value, anchored top-right. */
const DegreeBadge: React.FC<{ degrees: number }> = ({ degrees }) => (
    <g>
        <rect x={88} y={7} width={26} height={13} rx={6.5} fill="#3E3D3B" opacity="0.92" />
        <text
            x={101}
            y={16}
            fontSize="7.5"
            fill="#FFFFFF"
            fontFamily="system-ui, -apple-system, sans-serif"
            fontWeight={600}
            textAnchor="middle"
        >
            {degrees}°
        </text>
    </g>
);

function makeRotationIllustration(degrees: number, gradPrefix: string): React.FC {
    const Component: React.FC = () => {
        const rotY = (degrees * Math.PI) / 180;
        const tiltX = (TILT_X_DEG * Math.PI) / 180;

        // Local cube vertices, side = 1, centered at origin.
        // Order: FBL, FBR, FTR, FTL, BBL, BBR, BTR, BTL.
        const local: Vec3[] = [
            [-0.5, -0.5, -0.5], [0.5, -0.5, -0.5], [0.5, 0.5, -0.5], [-0.5, 0.5, -0.5],
            [-0.5, -0.5, 0.5], [0.5, -0.5, 0.5], [0.5, 0.5, 0.5], [-0.5, 0.5, 0.5],
        ];
        const raw = local.map((v) => project3D(v, rotY, tiltX));

        // Auto-center horizontally on CUBE_CX using the projected
        // bounding box, and ground the cube so its lowest projected
        // vertex sits exactly on HORIZON.
        const xs = raw.map((p) => p[0]);
        const ys = raw.map((p) => p[1]);
        const bboxCx = (Math.min(...xs) + Math.max(...xs)) / 2;
        const bboxMinY = Math.min(...ys);
        const screenOf = (p: [number, number]): [number, number] => [
            CUBE_CX + (p[0] - bboxCx) * SCALE,
            HORIZON + (bboxMinY - p[1]) * SCALE,
        ];
        const [FBL, FBR, FTR, FTL, , BBR, BTR, BTL] = raw.map(screenOf);

        const pts = (...verts: [number, number][]): string =>
            verts.map((p) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' ');

        return (
            <svg viewBox="0 0 120 90" style={baseStyle}>
                <defs>
                    <linearGradient id={`${gradPrefix}-f`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#CECDC9" />
                        <stop offset="100%" stopColor="#8A8987" />
                    </linearGradient>
                    <linearGradient id={`${gradPrefix}-s`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#6A6967" />
                        <stop offset="100%" stopColor="#3E3D3B" />
                    </linearGradient>
                    <linearGradient id={`${gradPrefix}-t`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#EEEDEA" />
                        <stop offset="100%" stopColor="#B0AFAC" />
                    </linearGradient>
                </defs>
                <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
                <Floor fill="#E4E2DE" />

                <ellipse cx={CUBE_CX} cy={HORIZON + 1.5} rx={17} ry={3} fill="#1F2022" opacity="0.22" />

                {/* Painter's order for a cube tilted slightly down and
                    rotated +θ around Y (right side comes forward toward
                    the camera): draw top (furthest due to tilt), then
                    right side, then front (closest). */}
                <polygon points={pts(FTL, FTR, BTR, BTL)} fill={`url(#${gradPrefix}-t)`} />
                {degrees > 0 && (
                    <polygon points={pts(FBR, BBR, BTR, FTR)} fill={`url(#${gradPrefix}-s)`} />
                )}
                <polygon points={pts(FBL, FBR, FTR, FTL)} fill={`url(#${gradPrefix}-f)`} />

                <DegreeBadge degrees={degrees} />
            </svg>
        );
    };
    Component.displayName = `HeroRotation${degrees}`;
    return Component;
}

export const HeroRotation15 = makeRotationIllustration(15, 'hr-15');
export const HeroRotation20 = makeRotationIllustration(20, 'hr-20');
export const HeroRotation25 = makeRotationIllustration(25, 'hr-25');
export const HeroRotation30 = makeRotationIllustration(30, 'hr-30');
export const HeroRotation40 = makeRotationIllustration(40, 'hr-40');
export const HeroRotation45 = makeRotationIllustration(45, 'hr-45');

export const HERO_ROTATION_ILLUSTRATIONS: Record<string, React.FC> = {
    '15': HeroRotation15,
    '20': HeroRotation20,
    '25': HeroRotation25,
    '30': HeroRotation30,
    '40': HeroRotation40,
    '45': HeroRotation45,
};

// ═════════════════════════════════════════════════════════════════
// SURFACE variants — the subject is a FLAT TILE (no depth), not a
// cube. Same 3D projection pipeline, but we only have one plane of
// 4 vertices (the tile face at z=0). As rotation grows the tile
// foreshortens into a trapezoid instead of revealing a side face.
// ═════════════════════════════════════════════════════════════════

function makeTileRotationIllustration(degrees: number, gradPrefix: string): React.FC {
    const Component: React.FC = () => {
        const rotY = (degrees * Math.PI) / 180;
        const tiltX = (TILT_X_DEG * Math.PI) / 180;

        // Tile has no depth — only the front-face plane (z=0).
        const local: Vec3[] = [
            [-0.5, -0.5, 0], // BL
            [0.5, -0.5, 0],  // BR
            [0.5, 0.5, 0],   // TR
            [-0.5, 0.5, 0],  // TL
        ];
        const raw = local.map((v) => project3D(v, rotY, tiltX));

        const xs = raw.map((p) => p[0]);
        const ys = raw.map((p) => p[1]);
        const bboxCx = (Math.min(...xs) + Math.max(...xs)) / 2;
        const bboxMinY = Math.min(...ys);
        const screenOf = (p: [number, number]): [number, number] => [
            CUBE_CX + (p[0] - bboxCx) * SCALE,
            HORIZON + (bboxMinY - p[1]) * SCALE,
        ];
        const [BL, BR, TR, TL] = raw.map(screenOf);

        // Two horizontal veining lines across the tile face, projected
        // through the same rotation so they follow the foreshortening.
        const veinsLocal: Array<[Vec3, Vec3]> = [
            [[-0.42, 0.22, 0], [0.42, 0.24, 0]],
            [[-0.42, -0.18, 0], [0.42, -0.16, 0]],
        ];
        const veinsScreen = veinsLocal.map(
            ([a, b]) =>
                [screenOf(project3D(a, rotY, tiltX)), screenOf(project3D(b, rotY, tiltX))] as [
                    [number, number],
                    [number, number],
                ],
        );

        const pts = (...verts: [number, number][]): string =>
            verts.map((p) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' ');

        return (
            <svg viewBox="0 0 120 90" style={baseStyle}>
                <defs>
                    <linearGradient id={`${gradPrefix}-tile`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#EEEDEA" />
                        <stop offset="100%" stopColor="#ACAAA6" />
                    </linearGradient>
                </defs>
                <rect x="0" y="0" width="120" height="90" fill="#F4F4F2" />
                <Floor fill="#E4E2DE" />

                <ellipse cx={CUBE_CX} cy={HORIZON + 1.5} rx={17} ry={3} fill="#1F2022" opacity="0.22" />

                {/* Flat tile — 4-vertex polygon, no top/side faces. */}
                <polygon
                    points={pts(BL, BR, TR, TL)}
                    fill={`url(#${gradPrefix}-tile)`}
                    stroke="#8A8986"
                    strokeWidth="0.5"
                />

                {/* Veining follows the rotated surface. */}
                {veinsScreen.map((v, i) => (
                    <line
                        key={i}
                        x1={v[0][0]}
                        y1={v[0][1]}
                        x2={v[1][0]}
                        y2={v[1][1]}
                        stroke="#999894"
                        strokeWidth="0.4"
                        opacity={0.55 - i * 0.1}
                        strokeLinecap="round"
                    />
                ))}

                <DegreeBadge degrees={degrees} />
            </svg>
        );
    };
    Component.displayName = `HeroRotationSurface${degrees}`;
    return Component;
}

export const HeroRotationSurface15 = makeTileRotationIllustration(15, 'hr-s-15');
export const HeroRotationSurface20 = makeTileRotationIllustration(20, 'hr-s-20');
export const HeroRotationSurface25 = makeTileRotationIllustration(25, 'hr-s-25');
export const HeroRotationSurface30 = makeTileRotationIllustration(30, 'hr-s-30');
export const HeroRotationSurface40 = makeTileRotationIllustration(40, 'hr-s-40');
export const HeroRotationSurface45 = makeTileRotationIllustration(45, 'hr-s-45');

export const HERO_ROTATION_ILLUSTRATIONS_SURFACE: Record<string, React.FC> = {
    '15': HeroRotationSurface15,
    '20': HeroRotationSurface20,
    '25': HeroRotationSurface25,
    '30': HeroRotationSurface30,
    '40': HeroRotationSurface40,
    '45': HeroRotationSurface45,
};
